import { useMemo, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass, inputClass } from "@/components/common/surfaceClasses";
import { usePagination, type PageSize } from "@/components/common/usePagination";
import type { SsdDealerMapBoard, SsdUnmappedDealer } from "@/lib/ssdSalesClient";
import { stackUnmappedLots } from "@/lib/ssd/stackUnmappedLots";
import { AudienceDataDealerMapCreateFields } from "@/components/audience-data/AudienceDataDealerMapCreateFields";

const UNMAPPED_PAGE_SIZES = [5, 10, 25] as const;

type Mode = "existing" | "create" | "release";

function lotLabel(name: string): string {
  const i = name.toLowerCase().lastIndexOf(" of ");
  return i >= 0 ? name.slice(i + 4).trim() : name.trim();
}

function applyLot(row: SsdUnmappedDealer): {
  dealerId: string;
  orgId: string;
  mode: Mode;
  locationId: string;
  name: string;
  city: string;
  state: string;
  address: string;
  postal: string;
} {
  return {
    dealerId: row.source_dealer_id,
    orgId: row.suggested_org_id ?? "",
    mode: row.suggested_action === "match" ? "existing" : "create",
    locationId: row.suggested_location_id ?? "",
    name: lotLabel(row.source_dealer_name) || row.source_dealer_name,
    city: row.city,
    state: row.state,
    address: row.street,
    postal: row.postal_code,
  };
}

export function AudienceDataDealerMapCard(props: {
  board: SsdDealerMapBoard;
  mapping: boolean;
  onMap: (input: {
    source_dealer_id: string;
    source_dealer_name: string;
    selling_org_id: string;
    location_id?: string | null;
    create_location?: {
      name: string;
      address_1?: string;
      city?: string;
      state?: string;
      postal_code?: string;
    } | null;
    not_our_location?: boolean;
  }) => Promise<boolean>;
}) {
  const first = props.board.unmapped[0] ?? null;
  const seed = first ? applyLot(first) : null;
  const [dealerId, setDealerId] = useState(seed?.dealerId ?? "");
  const [orgId, setOrgId] = useState(seed?.orgId || props.board.subscribers[0]?.org_id || "");
  const [mode, setMode] = useState<Mode>(seed?.mode ?? "create");
  const [locationId, setLocationId] = useState(seed?.locationId ?? "");
  const [name, setName] = useState(seed?.name ?? "");
  const [city, setCity] = useState(seed?.city ?? "");
  const [state, setState] = useState(seed?.state ?? "");
  const [address, setAddress] = useState(seed?.address ?? "");
  const [postal, setPostal] = useState(seed?.postal ?? "");

  const [open, setOpen] = useState(false);

  const dealerOptions = useMemo(() => stackUnmappedLots(props.board.unmapped), [props.board.unmapped]);
  const page = usePagination(dealerOptions, 5);
  const selected = dealerOptions.find((d) => d.source_dealer_id === dealerId.trim()) ?? dealerOptions[0] ?? null;
  const org = props.board.subscribers.find((s) => s.org_id === orgId) ?? props.board.subscribers[0];
  const unmatched = dealerOptions.length;
  if (unmatched === 0 || props.board.subscribers.length === 0) return null;

  const pickDealer = (row: SsdUnmappedDealer) => {
    const next = applyLot(row);
    setDealerId(next.dealerId);
    setOrgId(next.orgId || org?.org_id || "");
    setMode(next.mode);
    setLocationId(next.locationId);
    setName(next.name);
    setCity(next.city);
    setState(next.state);
    setAddress(next.address);
    setPostal(next.postal);
  };

  return (
    <SectionCard
      className="min-w-0 overflow-hidden !border-amber-400 bg-amber-50 dark:!border-amber-500 dark:bg-amber-950/35"
      title="Unrecognized sales locations"
      description={
        open
          ? "This Sales Feed lot is not in Turbo yet. Create or match a location on the selling organization — or, if it is not yours, send it to the manufacturer. Do not create a dealer location just to file manufacturer-owned lots."
          : `${unmatched} lot${unmatched === 1 ? "" : "s"} need to be matched or sent to the manufacturer.`
      }
      rightSlot={
        <button
          type="button"
          className="rounded-lg border border-amber-500 bg-amber-100 px-3 py-1.5 text-sm font-semibold text-amber-950 hover:bg-amber-200 dark:border-amber-400 dark:bg-amber-900/70 dark:text-amber-50 dark:hover:bg-amber-800"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? "Collapse" : `${unmatched} unmatched`}
        </button>
      }
    >
      {open ? (
        <>
      {selected ? (
        <p className="text-sm text-gray-700 dark:text-zinc-300">
          {selected.source_dealer_name || "Unnamed lot"} ({selected.source_dealer_id}) has {selected.fact_count}{" "}
          unmatched sale{selected.fact_count === 1 ? "" : "s"}.
          {selected.suggested_action === "create" ? " No Turbo location matches this name." : ""}
          {selected.suggested_action === "match" ? " A current location looks like a match." : ""}
        </p>
      ) : null}
      <div className="space-y-2">
        <p className="text-sm text-gray-700 dark:text-zinc-300">Unmapped lots</p>
        <ul className="divide-y divide-amber-200 rounded-lg border border-amber-300 dark:divide-amber-800 dark:border-amber-700">
          {page.paginatedItems.map((d) => {
            const active = d.source_dealer_id === selected?.source_dealer_id;
            return (
              <li key={d.source_dealer_id}>
                <button
                  type="button"
                  className={`w-full px-3 py-2 text-left text-sm ${active ? "bg-amber-100 dark:bg-amber-900/50" : ""}`}
                  onClick={() => pickDealer(d)}
                >
                  <span className="font-medium text-gray-900 dark:text-zinc-100">
                    {d.source_dealer_name || "Unnamed lot"}
                  </span>
                  <span className="block text-xs text-gray-600 dark:text-zinc-400">
                    {d.source_dealer_id} · {d.fact_count} sale{d.fact_count === 1 ? "" : "s"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <PaginationBar
          pageIndex={page.pageIndex}
          pageCount={page.pageCount}
          pageSize={page.pageSize}
          totalCount={page.totalCount}
          firstShown={page.firstShown}
          lastShown={page.lastShown}
          onPageChange={page.setPageIndex}
          onPageSizeChange={(n) => {
            if ((UNMAPPED_PAGE_SIZES as readonly number[]).includes(n)) page.setPageSize(n as PageSize);
          }}
          pageSizeOptions={UNMAPPED_PAGE_SIZES}
          noun="lots"
          alwaysShow
        />
      </div>
      {mode !== "release" ? (
      <label className="block min-w-0 text-sm text-gray-700 dark:text-zinc-300">
        Organization
        <select className={`mt-1 ${inputClass}`} value={org?.org_id ?? ""} onChange={(e) => setOrgId(e.target.value)}>
          {props.board.subscribers.map((s) => (
            <option key={s.org_id} value={s.org_id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      ) : null}
      <div className="flex min-w-0 flex-wrap gap-3 text-sm text-gray-700 dark:text-zinc-300">
        <label className="flex items-center gap-2">
          <input type="radio" checked={mode === "create"} onChange={() => setMode("create")} />
          Create a new location
        </label>
        <label className="flex items-center gap-2">
          <input type="radio" checked={mode === "existing"} onChange={() => setMode("existing")} />
          Match an existing location
        </label>
        {props.board.can_release ? (
          <label className="flex items-center gap-2">
            <input type="radio" checked={mode === "release"} onChange={() => setMode("release")} />
            Not our location
          </label>
        ) : null}
      </div>
      {mode === "release" ? (
        <p className="text-sm text-gray-700 dark:text-zinc-300">
          Send this lot to {props.board.manufacturer_name || "the manufacturer"} without creating a location here.
          They can map it as manufacturer-direct.
        </p>
      ) : null}
      {mode === "existing" ? (
        <label className="block min-w-0 text-sm text-gray-700 dark:text-zinc-300">
          Location
          <select className={`mt-1 ${inputClass}`} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">Org only</option>
            {(org?.locations ?? []).map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
                {loc.city ? ` · ${loc.city}` : ""}
              </option>
            ))}
          </select>
        </label>
      ) : mode === "create" ? (
        <AudienceDataDealerMapCreateFields
          name={name}
          street={address}
          city={city}
          state={state}
          postal={postal}
          onName={setName}
          onStreet={setAddress}
          onCity={setCity}
          onState={setState}
          onPostal={setPostal}
          disabled={props.mapping}
        />
      ) : null}
      <button
        type="button"
        className={ghostBtnClass}
        disabled={
          props.mapping ||
          !selected ||
          (mode !== "release" && !org) ||
          (mode === "create" && !name.trim())
        }
        onClick={() => {
          if (!selected) return;
          if (mode === "release") {
            void props.onMap({
              source_dealer_id: selected.source_dealer_id,
              source_dealer_name: selected.source_dealer_name || name.trim(),
              selling_org_id: props.board.manufacturer_org_id || "",
              not_our_location: true,
            });
            return;
          }
          if (!org) return;
          void props.onMap({
            source_dealer_id: selected.source_dealer_id,
            source_dealer_name: selected.source_dealer_name || name.trim(),
            selling_org_id: org.org_id,
            location_id: mode === "existing" && locationId ? locationId : null,
            create_location:
              mode === "create"
                ? { name: name.trim(), address_1: address, city, state, postal_code: postal }
                : null,
          });
        }}
      >
        {props.mapping
          ? "Saving…"
          : mode === "release"
            ? `Send to ${props.board.manufacturer_name || "manufacturer"}`
            : "Save mapping"}
      </button>
        </>
      ) : null}
    </SectionCard>
  );
}
